import isPromise from 'is-promise';
import store from 'store2';
import config from 'config';

const EXPIRED = 1000 * 60 * 60 * 24 * 14;

export const applicationServerKey = () => urlB64ToUint8Array(config.vapid_public_key);
export const isNotificationSupport = 'Notification' in window;
export const isPushManagerSupport = 'PushManager' in window;
export const isServiceWorkerSupport = 'serviceWorker' in navigator;

const SERVICE_WORKER_NOT_SUPPORTED = 'serviceWorker isn\'t supported';
const PUSH_MANAGER_NOT_SUPPORTED = 'PushManager isn\'t supported';
const NOTIFICATION_NOT_SUPPORTED = 'Notification isn\'t supported';
const EMPTY_REGISTRATION = 'Empty serviceWorker registration';

export class PushMessagingError extends Error {
}

export function handleError(err) {
    if (err instanceof PushMessagingError) {
        'console' in window && console.warn(err.message);
    } else {
        throw err;
    }
}

export function urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export async function getRegistration() {
    if (!isServiceWorkerSupport) throw new PushMessagingError(SERVICE_WORKER_NOT_SUPPORTED);

    const registration = await navigator.serviceWorker.getRegistration();
    return registration;
}

export async function displayNotification(...args) {
    if (!isNotificationSupport) return;
    if (Notification.permission === 'granted') {
        const registration = await getRegistration();
        registration.showNotification(...args);
    }
}

export function requestPermission() {
    if (isPromise(Notification.requestPermission)) {
        return Notification.requestPermission();
    }

    return new Promise((resolve, reject) => {
        Notification.requestPermission((result) => {
            resolve(result);
        });
    });
}

export async function askPermission() {
    if (!isNotificationSupport) throw new PushMessagingError(NOTIFICATION_NOT_SUPPORTED);

    const expiredAt = store.get('notification_requested_at', 0);
    const isExpired = expiredAt + EXPIRED < Date.now();

    if (Notification.permission !== 'granted' && isExpired) {
        const result = await requestPermission();
        store.set('notification_requested_at', Date.now());
        return result === 'granted';
    }

    return Notification.permission === 'granted';
}

export async function isSubscribed() {
    if (!isPushManagerSupport) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    const registration = await getRegistration();
    if (!registration) throw new PushMessagingError(EMPTY_REGISTRATION);

    if (!registration.pushManager) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    let subscription = await registration.pushManager.getSubscription();

    return !!subscription;
}

export async function subscribe() {
    if (!isPushManagerSupport) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    const registration = await getRegistration();
    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(),
    });

    return subscription;
}

export async function getSubscription() {
    if (!isPushManagerSupport) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    const registration = await getRegistration();

    if (!registration) return;
    if (!registration.pushManager) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    let subscription = await registration.pushManager.getSubscription();

    return subscription;
}

export async function unsubscribe() {
    if (!isPushManagerSupport) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    const registration = await getRegistration();

    if (!registration) return;
    if (!registration.pushManager) throw new PushMessagingError(PUSH_MANAGER_NOT_SUPPORTED);

    let subscription = await registration.pushManager.getSubscription();

    await subscription.unsubscribe();

    return JSON.stringify(subscription);
}

export async function init() {
    try {
        const isGranted = await askPermission();
        let subscription = await getSubscription();

        if (subscription && !isGranted) {
            await unsubscribe();
            return;
        }
        if (!isGranted) return;
        if (!subscription) {
            subscription = await subscribe();
        }
        return subscription;
    } catch (err) {
        handleError(err);
    }
}
